import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, fileUrl, formatDate, formatIDR } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Plus, ShoppingBag, MapPin } from "lucide-react";

export default function Requests() {
  const { user } = useAuth();
  const isJastiper = user?.role === "jastiper";
  const isBuyer = user?.role === "buyer";
  const [requests, setRequests] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const country = params.get("country") || "";
  const mine = params.get("mine") === "true";

  useEffect(() => {
    api.get("/countries").then((r) => setCountries(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams();
    if (country) q.set("country", country);
    if (mine) q.set("mine", "true");
    api.get(`/requests?${q.toString()}`).then((r) => setRequests(r.data)).finally(() => setLoading(false));
  }, [country, mine]);

  const setCountry = (c) => {
    const p = new URLSearchParams(params);
    if (c) p.set("country", c); else p.delete("country");
    setParams(p);
  };
  const setMine = (m) => {
    const p = new URLSearchParams(params);
    if (m) p.set("mine", "true"); else p.delete("mine");
    setParams(p);
  };

  return (
    <div className="space-y-8" data-testid="requests-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <div className="label-caps text-zinc-500 mb-2">Buyer Requests</div>
          <h1 className="font-display text-4xl tracking-tight">
            {isBuyer && mine ? "My Requests" : "Open Requests"}
          </h1>
          <p className="text-zinc-600 mt-2">
            {isJastiper ? "Items buyers want — accept a request and earn." : "Items you've requested or browse from other buyers."}
          </p>
        </div>
        {isBuyer && (
          <Link to="/requests/new" className="btn-primary-flat" data-testid="create-request-btn">
            <Plus size={16} strokeWidth={1.5} /> Post Request
          </Link>
        )}
      </div>

      <div className="border border-zinc-200 bg-white p-4 flex flex-col sm:flex-row gap-3 sm:items-center">
        {isBuyer && (
          <div className="flex gap-2">
            <button onClick={() => setMine(false)} className={`px-4 py-2 text-sm border ${!mine ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white border-zinc-200"}`} data-testid="filter-all-requests">All</button>
            <button onClick={() => setMine(true)} className={`px-4 py-2 text-sm border ${mine ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white border-zinc-200"}`} data-testid="filter-my-requests">Mine</button>
          </div>
        )}
        <div className="flex-1" />
        <select value={country} onChange={(e) => setCountry(e.target.value)} className="input-flat sm:w-64" data-testid="filter-requests-country">
          <option value="">All Countries</option>
          {countries.map((c) => <option key={c.code} value={c.name}>{c.flag} {c.name}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-center py-20 text-zinc-500">Loading...</div>
      ) : requests.length === 0 ? (
        <div className="border border-zinc-200 bg-white py-20 text-center" data-testid="empty-requests">
          <ShoppingBag size={32} strokeWidth={1.5} className="mx-auto text-zinc-400 mb-3" />
          <p className="text-zinc-500">No requests found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-fade">
          {requests.map((r) => (
            <Link key={r.id} to={`/requests/${r.id}`} className="grid-card block" data-testid={`request-card-${r.id}`}>
              <div className="aspect-[16/10] bg-zinc-100 overflow-hidden">
                {r.photo_path ? (
                  <img src={fileUrl(r.photo_path)} alt={r.item_name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-zinc-50 to-zinc-100">
                    <ShoppingBag size={40} strokeWidth={1} className="text-zinc-300" />
                  </div>
                )}
              </div>
              <div className="p-5 space-y-2">
                <div className="label-caps flex items-center gap-1.5" style={{color: "var(--app-muted)"}}><MapPin size={12} strokeWidth={1.5} /> Dari {r.source_country}</div>
                <div className="font-display text-lg tracking-tight truncate">{r.item_name}</div>
                <p className="text-sm line-clamp-2" style={{color: "var(--app-muted)"}}>{r.description}</p>
                <div className="border-t pt-3 flex justify-between items-center" style={{borderColor: "var(--app-border)"}}>
                  <div className="font-mono text-sm">
                    <div className="text-xs" style={{color: "var(--app-muted)"}}>Penawaran</div>
                    <div className="font-semibold" style={{color: "var(--app-brand)"}}>
                      {r.quoted_price ? formatIDR(r.quoted_price) : "—"}
                    </div>
                  </div>
                  <RequestStatusPill status={r.status} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function RequestStatusPill({ status }) {
  const map = {
    pending_price: { bg: "#FEF3C7", text: "#92400E", label: "Menunggu Harga" },
    priced: { bg: "#E0E7FF", text: "#3730A3", label: "Sudah Diberi Harga" },
    agreed: { bg: "#DCFCE7", text: "#166534", label: "Disepakati" },
    declined: { bg: "#FEE2E2", text: "#991B1B", label: "Ditolak" },
  };
  const s = map[status] || map.pending_price;
  return (
    <span
      className="label-caps px-2 py-1 font-mono text-[10px]"
      style={{ backgroundColor: s.bg, color: s.text }}
      data-testid={`request-status-pill-${status}`}
    >
      {s.label}
    </span>
  );
}
